import type { AbilityDef, Content, TalentDef } from '@gra/content';
import { computeAbilityStats, evolutionTarget, upgradeTarget, type GameState, type PullResult } from '@gra/engine';
import { abilityIcon, relicIcon, statIcon } from './ability-icons';
import { describeModifier } from './format';
import { openSettings } from './settings-ui';
import { el, formatTime } from './dom';
import { glyph, glyphChip } from './glyphs';

interface AbilitySlot {
  id: string;
  root: HTMLElement;
  sweep: HTMLElement;
  rank: HTMLElement;
}

const ROMAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII'];

/** Łupy z wyprawy do pokazania w podsumowaniu. */
export interface RunLoot {
  gold: number;
  items: PullResult[];
}

/** Interfejs wyprawy: ramka postaci, pasek umiejętności, doświadczenie, wybór talentu, pauza, podsumowanie. */
export class RunUi {
  private readonly hud = el('div', 'hud');
  private readonly levelUp = el('div', 'screen levelup');
  private readonly pause = el('div', 'screen pause');
  private readonly gameOver = el('div', 'screen gameover');
  private readonly hurt = el('div', 'hurt');

  private readonly portrait = el('div', 'portrait');
  private readonly heroName = el('div', 'unit-name');
  private readonly levelBadge = el('div', 'level-badge');
  private readonly hpFill = el('div', 'fill');
  private readonly hpText = el('span');
  private readonly shieldFill = el('div', 'shield');
  /** Przedmioty wyprawy pod ramką postaci; dotknięcie ikony pokazuje opis. */
  private readonly relicBar = el('div', 'relic-bar');
  private readonly relicTip = el('div', 'relic-tip frame');
  private relicCount = -1;
  private readonly xpFill = el('div', 'fill');
  private readonly xpText = el('span');
  private readonly timeText = el('div', 'timer');
  private readonly powerText = el('div', 'run-power');
  private readonly eventText = el('div', 'run-event');
  private readonly killsText = el('div', 'kills');
  private readonly goldText = el('div', 'gold-count');
  private readonly toasts = el('div', 'loot-toasts');
  private readonly bossBar = el('div', 'boss-bar');
  private readonly bossName = el('div', 'boss-name');
  private readonly bossFill = el('div', 'fill');
  private readonly bossText = el('span');
  private readonly nextBoss = el('div', 'next-boss');
  private readonly banner = el('div', 'boss-banner');
  private readonly markers = el('div', 'markers');
  private markerPool: HTMLElement[] = [];
  private readonly zoneText = el('div', 'zone-name');
  private readonly abilityBar = el('div', 'ability-bar');
  private slots: AbilitySlot[] = [];

  private pick: ((talentId: string) => void) | null = null;
  private choices: string[] = [];
  levelUpOpen = false;
  gameOverOpen = false;
  /** Przycisk pauzy na ekranie (dla dotyku) i „Wróć do gry” w menu pauzy. */
  onTogglePause: (() => void) | null = null;

  constructor(private readonly content: Content) {
    const unit = el('div', 'unit-frame frame');
    const hp = el('div', 'bar hp');
    hp.append(this.hpFill, this.shieldFill, this.hpText);
    const info = el('div', 'unit-info');
    info.append(this.heroName, hp);
    this.portrait.append(this.levelBadge);
    unit.append(this.portrait, info);

    const top = el('div', 'top-banner frame');
    const counters = el('div', 'counters');
    counters.append(this.killsText, this.goldText, this.powerText);
    top.append(this.timeText, counters);

    const xp = el('div', 'xp-bar');
    xp.append(this.xpFill, this.xpText);

    const bossHp = el('div', 'bar boss-hp');
    bossHp.append(this.bossFill, this.bossText);
    this.bossBar.append(this.bossName, bossHp);
    top.append(this.nextBoss, this.eventText);
    const pauseBtn = el('button', 'pause-btn frame', 'II');
    pauseBtn.setAttribute('aria-label', 'Pauza');
    pauseBtn.addEventListener('click', () => this.onTogglePause?.());
    this.relicTip.hidden = true;
    this.hud.append(this.relicBar, this.relicTip);
    this.hud.append(this.markers, unit, top, this.bossBar, this.abilityBar, xp, this.toasts, this.banner, pauseBtn);
    const resumeBtn = el('button', 'btn', 'Wróć do gry');
    resumeBtn.addEventListener('click', () => this.onTogglePause?.());
    const settingsBtn = el('button', 'btn ghost', 'Ustawienia');
    settingsBtn.addEventListener('click', openSettings);
    this.pause.append(el('h2', 'title-glow', 'Pauza'), resumeBtn, settingsBtn, el('p', 'hint key-hint', 'Esc — wróć do gry'));
    this.hideAll();
    document.getElementById('ui')!.append(this.hurt, this.hud, this.levelUp, this.pause, this.gameOver);

    window.addEventListener('keydown', (e) => {
      if (!this.levelUpOpen || !this.pick) return;
      const choice = this.choices[['Digit1', 'Digit2', 'Digit3', 'Digit4', 'Digit5'].indexOf(e.code)];
      if (choice) this.pick(choice);
    });
  }

  show(s: GameState): void {
    this.hideAll();
    const cls = this.content.classes[s.classId]!;
    this.portrait.style.setProperty('--accent', cls.color);
    this.portrait.dataset.initial = cls.name[0] ?? '?';
    this.heroName.textContent = cls.name;
    this.zoneText.textContent = this.content.zones[s.zoneId]?.name ?? '';
    // Moc ekwipunku na wyprawie; czerwona, gdy za niska jak na strefę (wrogowie są wtedy twardsi).
    this.powerText.textContent = `⚔ ${s.power}`;
    this.powerText.title = 'Moc ekwipunku';
    this.powerText.classList.toggle('low', s.powerPenalty > 1);
    this.slots = [];
    this.relicCount = -1;
    this.relicTip.hidden = true;
    this.abilityBar.replaceChildren();
    this.toasts.replaceChildren();
    this.banner.classList.remove('on');
    this.hud.hidden = false;
  }

  /** Ramka portretu z osiągnięć (null = zwykła złota). */
  setFrame(color: string | null): void {
    if (color) this.portrait.style.setProperty('--frame', color);
    else this.portrait.style.removeProperty('--frame');
    this.portrait.classList.toggle('framed', !!color);
  }

  hideAll(): void {
    for (const node of [this.hud, this.levelUp, this.pause, this.gameOver]) node.hidden = true;
    this.levelUpOpen = false;
    this.gameOverOpen = false;
    this.pick = null;
  }

  update(s: GameState): void {
    const p = s.player;
    this.hpFill.style.width = `${(p.hp / p.stats.maxHealth) * 100}%`;
    this.hpText.textContent = `${Math.ceil(p.hp)} / ${Math.round(p.stats.maxHealth)}`;
    this.shieldFill.style.width = `${Math.min(1, p.shield / p.stats.maxHealth) * 100}%`;
    if (s.relics.length !== this.relicCount) this.renderRelics(s);
    this.xpFill.style.width = `${Math.min(1, s.xp / s.xpToNext) * 100}%`;
    this.xpText.textContent = `${Math.floor(s.xp)} / ${s.xpToNext} PD`;
    this.levelBadge.textContent = String(s.level);
    this.timeText.textContent = formatTime(s.time);
    // Trwające wydarzenie wyprawy: nazwa i pozostały czas pod zegarem.
    const ev = s.runEvent;
    const evName = ev ? s.content.game.runEvents?.list.find((d) => d.id === ev.id)?.name : undefined;
    this.eventText.hidden = !evName;
    if (ev && evName) this.eventText.textContent = `${evName} · ${Math.ceil(ev.left)} s`;
    this.killsText.textContent = `☠ ${s.kills}`;
    this.goldText.replaceChildren(el('span', 'coin'), el('b', '', String(s.gold)));
    this.updateAbilities(s);
    this.updateBoss(s);
  }

  /** Pasek życia bossa u góry ekranu i odliczanie do następnego bossa. */
  private updateBoss(s: GameState): void {
    const boss = s.enemies.find((e) => s.bossIds.includes(e.id));
    this.bossBar.hidden = !boss;
    if (boss) {
      this.bossName.textContent = this.content.enemies[boss.defId]?.name ?? '';
      this.bossFill.style.width = `${Math.max(0, boss.hp / boss.maxHp) * 100}%`;
      this.bossText.textContent = `${Math.ceil(Math.max(0, boss.hp)).toLocaleString('pl-PL')}`;
    }
    const next = this.content.zones[s.zoneId]?.bosses[s.bossesSpawned];
    this.nextBoss.hidden = !next || !!boss;
    if (next) {
      const name = this.content.enemies[next.enemy]?.name ?? 'Boss';
      // Korona + czas do bossa; nazwę mówi podpowiedź (title), żeby nie zaśmiecać ekranu.
      const t = formatTime(Math.max(0, next.at - s.time));
      if (this.nextBoss.dataset.t !== t) {
        this.nextBoss.dataset.t = t;
        this.nextBoss.replaceChildren(glyphChip('crown', t, next.final ? 'final' : ''));
        this.nextBoss.title = next.final ? `Boss końcowy: ${name}` : name;
      }
    }
  }

  /** Strzałki na krawędzi ekranu wskazujące skrzynie i bossa poza widokiem. */
  updateMarkers(list: { x: number; y: number; angle: number; kind: 'chest' | 'boss' }[]): void {
    while (this.markerPool.length < list.length) {
      const m = el('div', 'marker');
      m.append(el('span', 'marker-arrow'), el('span', 'marker-icon'));
      this.markers.append(m);
      this.markerPool.push(m);
    }
    this.markerPool.forEach((m, i) => {
      const it = list[i];
      m.hidden = !it;
      if (!it) return;
      m.className = `marker ${it.kind}`;
      m.style.transform = `translate(${it.x}px, ${it.y}px)`;
      (m.firstChild as HTMLElement).style.transform = `rotate(${it.angle}rad) translateX(20px)`;
    });
  }

  /** Duży napis na środku ekranu (przybycie bossa, zwycięstwo). */
  announce(title: string, subtitle: string): void {
    this.banner.replaceChildren(el('div', 'banner-sub', subtitle), el('div', 'banner-title', title));
    this.banner.classList.remove('on');
    void this.banner.offsetWidth; // restart animacji
    this.banner.classList.add('on');
  }

  /** Pasek umiejętności: ikona i „zegar” odnowienia jak w klasycznych RPG. */
  private updateAbilities(s: GameState): void {
    // Pasek budujemy od nowa, gdy zmieni się lista umiejętności (nowa umiejętność albo ewolucja).
    if (this.slots.length !== s.abilities.length || s.abilities.some((a, i) => this.slots[i]?.id !== a.id)) {
      this.slots = s.abilities.map((a, i) => {
        const def = this.content.abilities[a.id]!;
        const root = el('div', 'ability-slot frame');
        root.title = `${def.name} — ${def.description}`;
        const img = el('img');
        img.src = abilityIcon(def);
        img.alt = def.name;
        const sweep = el('div', 'sweep');
        const rank = el('span', 'rank');
        root.append(img, sweep, el('span', 'keybind', String(i + 1)), rank);
        return { id: a.id, root, sweep, rank };
      });
      this.abilityBar.replaceChildren(...this.slots.map((sl) => sl.root));
    }
    s.abilities.forEach((a, i) => {
      const slot = this.slots[i];
      const def = this.content.abilities[a.id];
      if (!slot || !def) return;
      const cd = computeAbilityStats(def, s.modifiers, a.rank).cooldown;
      const roman = def.evolved ? '★' : a.rank > 1 ? ROMAN[a.rank] ?? String(a.rank) : '';
      slot.root.classList.toggle('evolved', def.evolved);
      if (slot.rank.textContent !== roman) slot.rank.textContent = roman;
      const left = Math.max(0, Math.min(1, a.cooldownLeft / cd));
      slot.sweep.style.setProperty('--left', `${left * 360}deg`);
      slot.root.classList.toggle('ready', left === 0);
    });
  }

  /** Ikony przedmiotów wyprawy (te same zebrane kilka razy — jedna ikona z licznikiem). */
  private renderRelics(s: GameState): void {
    this.relicCount = s.relics.length;
    const counts = new Map<string, number>();
    for (const r of s.relics) counts.set(r.relicId, (counts.get(r.relicId) ?? 0) + 1);
    this.relicBar.replaceChildren();
    for (const [id, n] of counts) {
      const def = this.content.relics[id];
      const r = def && this.content.rarities[def.rarity];
      if (!def || !r) continue;
      const b = el('button', 'relic-slot');
      b.style.setProperty('--rarity', r.color);
      const img = el('img');
      img.src = relicIcon(def, r.color);
      b.append(img);
      if (n > 1) b.append(el('span', 'relic-count', `×${n}`));
      b.title = `${def.name} — ${def.description}`;
      b.addEventListener('click', () => this.showRelicTip(id, n));
      this.relicBar.append(b);
    }
  }

  private showRelicTip(relicId: string, count: number): void {
    const def = this.content.relics[relicId];
    const r = def && this.content.rarities[def.rarity];
    if (!def || !r) return;
    // Drugie dotknięcie tej samej ikony chowa opis.
    if (!this.relicTip.hidden && this.relicTip.dataset.relic === relicId) {
      this.relicTip.hidden = true;
      return;
    }
    this.relicTip.dataset.relic = relicId;
    this.relicTip.style.setProperty('--rarity', r.color);
    this.relicTip.replaceChildren(
      el('b', '', count > 1 ? `${def.name} ×${count}` : def.name),
      el('span', 'rarity-name', r.name),
      el('p', 'hint', def.description),
      el('div', 'relic-mods', def.modifiers.map(describeModifier).join(' · ')),
    );
    this.relicTip.hidden = false;
    clearTimeout(this.tipTimer);
    this.tipTimer = window.setTimeout(() => (this.relicTip.hidden = true), 5000);
  }
  private tipTimer = 0;

  /** Komunikat o przedmiocie wyprawy: nazwa, rzadkość i co daje. */
  relicToast(relicId: string): void {
    const def = this.content.relics[relicId];
    const r = def && this.content.rarities[def.rarity];
    if (!def || !r) return;
    const toast = el('div', 'loot-toast');
    toast.style.setProperty('--rarity', r.color);
    const ico = el('img', 'toast-ico');
    ico.src = relicIcon(def, r.color);
    const txt = el('div');
    txt.append(el('b', '', def.name), el('span', 'rarity-name', def.modifiers.map(describeModifier).join(' · ')));
    toast.classList.add('with-ico');
    toast.append(ico, txt);
    this.toasts.append(toast);
    setTimeout(() => toast.classList.add('out'), 2600);
    setTimeout(() => toast.remove(), 3200);
  }

  /** Komunikat o znalezionym przedmiocie, w kolorze rzadkości. */
  lootToast(itemId: string, rarity: string): void {
    const def = this.content.items[itemId];
    const r = this.content.rarities[rarity];
    if (!def || !r) return;
    const toast = el('div', 'loot-toast');
    toast.style.setProperty('--rarity', r.color);
    toast.append(el('b', '', def.name), el('span', 'rarity-name', r.name));
    this.toasts.append(toast);
    setTimeout(() => toast.classList.add('out'), 2600);
    setTimeout(() => toast.remove(), 3200);
  }

  flashHurt(): void {
    if (this.hurt.classList.contains('on')) return;
    this.hurt.classList.add('on');
    setTimeout(() => this.hurt.classList.remove('on'), 180);
  }

  setPaused(paused: boolean): void {
    this.pause.hidden = !paused;
  }

  showLevelUp(s: GameState, onPick: (talentId: string) => void): void {
    this.choices = s.pendingChoices ?? [];
    this.pick = onPick;
    this.levelUp.replaceChildren(el('h2', 'title-glow', `Poziom ${s.level}!`));
    const list = el('div', 'cards');
    this.choices.forEach((id, i) => {
      const evolving = evolutionTarget(id);
      const from = evolving ? this.content.abilities[evolving] : undefined;
      const into = from?.evolution ? this.content.abilities[from.evolution] : undefined;
      if (from && into) {
        list.append(this.evolutionCard(from, into, i + 1, () => onPick(id)));
        return;
      }
      const upgraded = upgradeTarget(id);
      const ability = upgraded ? this.content.abilities[upgraded] : undefined;
      const state = s.abilities.find((a) => a.id === upgraded);
      if (ability && state) {
        list.append(this.upgradeCard(ability, state.rank, s.content.game.abilityMaxRank, i + 1, () => onPick(id)));
        return;
      }
      const t = this.content.talents[id];
      if (t) list.append(this.talentCard(t, s.talents[id] ?? 0, i + 1, () => onPick(id)));
    });
    this.levelUp.append(list);
    this.levelUp.hidden = false;
    this.levelUpOpen = true;
  }

  hideLevelUp(): void {
    this.levelUp.hidden = true;
    this.levelUpOpen = false;
    this.pick = null;
  }

  showGameOver(s: GameState, reward: number, loot: RunLoot, previousBest: number, onRetry: () => void, onCamp: () => void): void {
    this.gameOverOpen = true;
    const camp = el('button', 'btn', 'Wróć do obozu');
    camp.addEventListener('click', onCamp);
    const retry = el('button', 'btn ghost', 'Jeszcze raz');
    retry.addEventListener('click', onRetry);
    const buttons = el('div', 'buttons');
    buttons.append(camp, retry);
    const box = el('div', 'panel-box frame summary');
    const stats = el('div', 'summary-stats');
    // Kafelki: symbol + liczba, nazwa tylko w podpowiedzi.
    const stat = (icon: 'clock' | 'star' | 'skull' | 'sword', label: string, value: string) => {
      const cell = el('div', 'summary-stat');
      cell.title = label;
      cell.append(glyph(icon), el('div', 'summary-value', value));
      stats.append(cell);
    };
    stat('clock', 'Czas', formatTime(s.time));
    stat('star', 'Poziom', String(s.level));
    stat('skull', 'Pokonani', String(s.kills));
    stat('sword', 'Obrażenia', Math.round(s.damageDealt).toLocaleString('pl-PL'));

    const talents = el('div', 'summary-talents');
    for (const [id, rank] of Object.entries(s.talents)) {
      const t = this.content.talents[id];
      if (!t || rank <= 0) continue;
      const chip = el('div', 'talent-chip');
      chip.title = `${t.name} (${rank}/${t.maxRank})`;
      const img = el('img');
      img.src = this.talentIconSrc(t);
      chip.append(img, el('span', '', String(rank)));
      talents.append(chip);
    }

    box.append(el('h2', s.won ? 'title-glow victory' : 'title-glow', s.won ? 'Zwycięstwo!' : 'Koniec wyprawy'), el('div', 'zone-name', this.content.zones[s.zoneId]?.name ?? ''), stats);
    if (s.time > previousBest && previousBest > 0) box.append(el('div', 'record', '★ Nowy rekord czasu! ★'));
    if (talents.childElementCount) box.append(talents);
    if (s.relics.length) {
      const relics = el('div', 'summary-relics');
      for (const r of s.relics) {
        const def = this.content.relics[r.relicId];
        const rar = def && this.content.rarities[def.rarity];
        if (!def || !rar) continue;
        const img = el('img');
        img.src = relicIcon(def, rar.color);
        img.title = def.name;
        relics.append(img);
      }
      relics.title = 'Przedmioty wyprawy przepadają po powrocie do obozu';
      box.append(relics);
    }
    if (loot.items.length) {
      const items = el('div', 'summary-loot');
      for (const { item, upgraded } of loot.items) {
        const def = this.content.items[item.itemId];
        const r = this.content.rarities[item.rarity];
        if (!def || !r) continue;
        const row = el('div', 'loot-row');
        row.style.setProperty('--rarity', r.color);
        row.append(el('b', '', def.name), el('span', 'hint', upgraded ? `${r.name} · poziom ${item.level}` : r.name));
        items.append(row);
      }
      box.append(items);
    }
    const rewards = el('div', 'reward');
    rewards.append(el('span', 'reward-gem', `+◆ ${reward}`));
    if (loot.gold > 0) {
      const g = el('span', 'reward-gold');
      g.append(document.createTextNode('+'), el('span', 'coin'), document.createTextNode(String(loot.gold)));
      rewards.append(g);
    }
    box.append(rewards, buttons);
    this.gameOver.replaceChildren(box);
    this.gameOver.hidden = false;
  }

  private talentIconSrc(t: TalentDef): string {
    const ability = t.grantsAbility ? this.content.abilities[t.grantsAbility] : undefined;
    return ability ? abilityIcon(ability) : statIcon(t.modifiers[0]?.stat ?? 'damage');
  }

/** Karta ewolucji: umiejętność na najwyższej randze zmienia się w nową formę. */
  private evolutionCard(from: AbilityDef, into: AbilityDef, key: number, onClick: () => void): HTMLButtonElement {
    const card = el('button', 'card talent frame evolution');
    card.style.setProperty('--evo', into.color);
    const icons = el('div', 'evo-icons');
    const a = el('img', 'talent-icon');
    a.src = abilityIcon(from);
    const b = el('img', 'talent-icon big');
    b.src = abilityIcon(into);
    icons.append(a, el('span', 'evo-arrow', '➜'), b);
    const head = el('div', 'talent-head');
    const title = el('div');
    title.append(el('div', 'evo-label', 'Ewolucja'), el('h3', '', into.name));
    head.append(title, el('span', 'key', String(key)));
    card.title = into.description;
    card.append(head, icons, el('p', 'card-line', into.description));
    card.addEventListener('click', onClick);
    return card;
  }

  /** Karta ulepszenia posiadanej umiejętności: następna ranga i co daje. */
  private upgradeCard(a: AbilityDef, rank: number, maxRank: number, key: number, onClick: () => void): HTMLButtonElement {
    const card = el('button', 'card talent frame upgrade');
    const img = el('img', 'talent-icon');
    img.src = abilityIcon(a);
    const head = el('div', 'talent-head');
    const title = el('div');
    title.append(el('h3', '', `${a.name} ${ROMAN[rank + 1] ?? rank + 1}`));
    head.append(img, title, el('span', 'key', String(key)));
    const extra = a.kind === 'projectile' && rank + 1 === maxRank ? ' · +1 pocisk' : '';
    const pips = el('div', 'pips');
    for (let i = 0; i < maxRank; i++) pips.append(el('span', i < rank ? 'pip on' : i === rank ? 'pip next' : 'pip'));
    card.title = `Ulepszenie · ranga ${rank + 1} z ${maxRank}: +10% obrażeń, −3% czasu odnowienia, +3% zasięgu${extra}`;
    card.append(head, el('p', 'card-line', `+10% obr. · −3% odn. · +3% zas.${extra}`), pips);
    card.addEventListener('click', onClick);
    return card;
  }

  private talentCard(t: TalentDef, rank: number, key: number, onClick: () => void): HTMLButtonElement {
    const card = el('button', `card talent frame${t.grantsAbility ? ' new-ability' : ''}`);
    const img = el('img', 'talent-icon');
    img.src = this.talentIconSrc(t);
    const head = el('div', 'talent-head');
    const title = el('div');
    title.append(el('h3', '', t.name));
    if (t.grantsAbility) title.append(el('span', 'new-tag', 'Nowa'));
    head.append(img, title, el('span', 'key', String(key)));
    // Kropki rang: wypełnione = już masz, świecąca = ta, którą właśnie bierzesz.
    const pips = el('div', 'pips');
    for (let i = 0; i < t.maxRank; i++) pips.append(el('span', i < rank ? 'pip on' : i === rank ? 'pip next' : 'pip'));
    // Jedna linia efektu: liczby z modyfikatorów, a pełny opis w podpowiedzi.
    const line = !t.grantsAbility && t.modifiers.length ? t.modifiers.map(describeModifier).join(' · ') : t.description;
    card.title = t.description;
    card.append(head, el('p', 'card-line', line), pips);
    card.addEventListener('click', onClick);
    return card;
  }
}
